import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { DetectResponse } from '../../types';

type UseDetectionsProps = {
  fileId?: string;
};

// Checking detections against the client's existing data can take longer than the default 25s on
// a first load. The API load balancer ends any request at 60s, so the timeout stays just under it.
// A retry picks up the reviews the timed-out load has already cached or still has running, so one
// is enough.
const FAST_CONFLICT_REVIEW_OPTIONS = { meta: { timeoutMs: 55_000 }, retry: 1 };

export function useInvalidateDetections() {
  const [searchParams] = useSearchParams();
  const { clientId } = useCurrentClient();
  const fileId = searchParams.get('fileId') ?? '';
  const queryClient = useQueryClient();

  const invalidateDetections = () =>
    queryClient.invalidateQueries({
      queryKey: ['clients', clientId, 'data-upload-files', fileId, 'detections'],
    });

  return { invalidateDetections };
}

export function useDetections({ fileId }: UseDetectionsProps) {
  const { clientId } = useCurrentClient();
  const { flagEnabledFastConflictReview } = useFlags();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<DetectResponse>({
    queryKey: ['clients', clientId, 'data-upload-files', fileId, 'detections'],
    enabled: !!clientId && !!fileId,
    staleTime: Infinity,
    ...(flagEnabledFastConflictReview ? FAST_CONFLICT_REVIEW_OPTIONS : {}),
  });

  return {
    detections: data?.detections || [],
    isLoading,
    isFetching,
    isError,
    error,
    refetchDetections: refetch,
  };
}
